import type { QueryResultWithLimit } from '@nx/constants';
import { APP_SCOPE, QUERY_TYPE } from '@nx/constants';
import type { ParseableError } from '@nx/errors';
import { isNeo4jError, isParseableError } from '@nx/errors';
import { StoreKit, runQuery } from '@nx/state';
import { UnknownErrors } from '@query/constants/errors';
import type { CypherRequestId } from '@query/redux/requests-slice';
import { registerAbortFunction, unregisterAbortFunction } from '@query/services/abortable-frame-service';
import { trackFailedCypherQuery, trackSuccessfulCypherQuery } from '@query/services/analytics';
import { cacheQueryResult } from '@query/services/frame-queryresult-service';
import { createAsyncThunk } from '@reduxjs/toolkit';

import type { ExecuteCommandPayload } from './command-thunks';

export type ExecutedCypherCommandPayload = {
  requestId: CypherRequestId;
  command: ExecuteCommandPayload;
  parameterSnapshot: Record<string, unknown>;
};

export type ExecutedCypherCommand = { requestId: CypherRequestId };

export const executeCypherCommandThunk = createAsyncThunk<
  ExecutedCypherCommand,
  ExecutedCypherCommandPayload,
  {
    rejectValue: { error: Partial<ParseableError>; aborted: boolean };
  }
>('stream/executeCypherCommand', async (payload: ExecutedCypherCommandPayload, { rejectWithValue }) => {
  const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
  const { requestId, command, parameterSnapshot } = payload;
  const database = command.connection?.database;

  try {
    const query = globalStore.dispatch(
      runQuery({
        recordLimit: command.recordLimit,
        useFrameworkStoreRecordLimit: command.recordLimit === undefined,
        parameters: parameterSnapshot,
        sessionConfig: { database: database ?? undefined },
        query: command.cmd,
        metadata: { appScope: APP_SCOPE.query, queryType: QUERY_TYPE.UserDirect },
        transactionMode: command.accessMode ?? 'write',
        // We don't want a timeout for user queries, if they run long the user will cancel them
        // setting undefined will use the timeout configured on the server
        // https://neo4j.com/docs/api/javascript-driver/current/typedef/index.html#static-typedef-TransactionConfig
        timeout: undefined,
        useBookmarkManager: command.useBookmarkManager ?? false,
      }),
    );

    registerAbortFunction(requestId, query.abort);

    const queryResult: QueryResultWithLimit = await query.unwrap();

    cacheQueryResult(requestId, queryResult);
    trackSuccessfulCypherQuery(queryResult, command.wasFormatted, command.useBookmarkManager, command.accessMode);

    return { requestId };
  } catch (error) {
    if (isParseableError(error)) {
      const errorCode = isNeo4jError(error) ? error.code : error.name;
      trackFailedCypherQuery(command.wasFormatted, errorCode);
      return rejectWithValue({ error, aborted: error.name === 'AbortError' });
    }

    trackFailedCypherQuery(command.wasFormatted, 'Unknown');
    return rejectWithValue({ error: UnknownErrors.UnknownError, aborted: false });
  } finally {
    unregisterAbortFunction(requestId);
  }
});
